/**
 * AgentCard Component
 * Card para mostrar información de un agente
 */

import {
  Card,
  CardContent,
  CardActionArea,
  Typography,
  Box,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  Stack,
  ListItemIcon,
  ListItemText,
  Button,
  useMediaQuery,
} from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import {
  Circle as CircleIcon,
  MoreVert as MoreVertIcon,
  SwapHoriz as SwapHorizIcon,
  Info as InfoIcon,
  Terminal as TerminalIcon,
  Login as LoginIcon,
} from '@mui/icons-material';
import { useState } from 'react';
import { fromNowEs } from '@/utils/dateUtils';
import { isOutdatedVersion } from '@/utils/version';
import { useLatestAgentVersion } from '@/hooks/useLatestAgentVersion';
import { OsLogo } from './OsLogo';
import { GroupAssignmentSelect } from '@/components/groups/GroupAssignmentSelect';
import type { Agent } from '@/types/agent';
import type { UserGroup } from '@/types/groups';

interface AgentCardProps {
  agent: Agent;
  selected?: boolean;
  onClick?: () => void;
  onConnect?: () => void;
  onConsole?: () => void;
  onDetails?: () => void;
  viewMode?: 'grid' | 'list' | 'listao';
  /**
   * When provided, renders the inline GroupAssignmentSelect at the bottom
   * of the card. The legacy flat-list flow (AgentList) doesn't pass this;
   * GroupTree passes the full groups list so each card can self-render.
   */
  groups?: UserGroup[];
}

export const AgentCard: React.FC<AgentCardProps> = ({
  agent,
  selected = false,
  onClick,
  onConnect,
  onConsole,
  onDetails,
  viewMode = 'grid',
  groups,
}) => {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleConnectClick = (event: React.MouseEvent) => {
    event.stopPropagation();
    onConnect?.();
  };

  const handleMenuAction = (action: () => void) => {
    return (event: React.MouseEvent) => {
      event.stopPropagation();
      handleMenuClose();
      action();
    };
  };

  const displayName = agent.hostname || agent.agentId;
  const archInfo = agent.arch || '';
  const lastSeenTime = agent.lastSeen ? fromNowEs(agent.lastSeen) : '';

  // Versión desactualizada: el manifest puede no estar listo todavía; en ese
  // caso tratamos como "no outdated" y ocultamos el chip.
  const latestVersion = useLatestAgentVersion();
  const outdated = isOutdatedVersion(agent.version, latestVersion);

  return (
    <Card
      sx={{
        border: selected ? 2 : 1,
        borderColor: selected ? 'primary.main' : 'divider',
        transition: 'all 0.2s',
        display: viewMode === 'listao' ? 'flex' : undefined,
        alignItems: viewMode === 'listao' ? 'center' : undefined,
        '&:hover': {
          borderColor: 'primary.main',
          boxShadow: 2,
        },
        width: '100%',
      }}
    >
      <CardActionArea component="div" onClick={onClick} sx={{ display: 'block' }}>
        <CardContent
          sx={{
            display: 'flex',
            gap: 2,
            alignItems: viewMode === 'listao' ? 'center' : 'flex-start',
            // `minWidth: 0` lets the row shrink below its intrinsic content
            // width so the middle text column can ellipsize long hostnames
            // on narrow viewports. Without it, flex children default to
            // `min-width: auto` and the row keeps its widest intrinsic
            // child, pushing the card past the device edge on mobile.
            minWidth: 0,
          }}
        >
          <OsLogo
            os={agent.os}
            osVersion={agent.osVersion}
            connected={agent.connected}
            size={viewMode === 'listao' ? 48 : 40}
          />

          <Box flex={1} minWidth={0}>
            {/* Inner row hosting the hostname. `minWidth: 0` on this Box
                is what actually lets the `<Typography noWrap>` ellipsize:
                the row's intrinsic width is the full hostname, and without
                minWidth:0 the parent flex chain refuses to shrink below
                it, so the ellipsis never kicks in. */}
            <Box display="flex" alignItems="center" gap={1} mb={0.5} minWidth={0}>
              <CircleIcon
                sx={{
                  fontSize: 12,
                  color: agent.connected ? 'success.main' : 'error.main',
                  flexShrink: 0,
                }}
              />
              <Typography
                variant="h6"
                component="div"
                noWrap
                sx={{ fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', minWidth: 0 }}
              >
                {displayName}
              </Typography>
            </Box>

            <Stack
              spacing={0.5}
              direction={viewMode === 'listao' ? 'row' : 'column'}
              alignItems={viewMode === 'listao' ? 'center' : 'flex-start'}
            >
              {archInfo && (
                <Typography variant="body2" color="text.secondary" noWrap>
                  {archInfo}
                </Typography>
              )}
              <Box display="flex" gap={1} flexWrap="wrap">
                {/* Versión: oculta por defecto; sólo aparece si está desactualizada,
                    en rojo suave para llamar la atención sin gritar. */}
                {outdated && (
                  <Chip
                    label={`v${agent.version} → v${latestVersion}`}
                    size="small"
                    aria-label={`Versión desactualizada: ${agent.version}, última ${latestVersion}`}
                    sx={{
                      height: 20,
                      fontSize: '0.7rem',
                      fontWeight: 600,
                      bgcolor: alpha(theme.palette.error.main, 0.1),
                      color: 'error.dark',
                      border: '1px solid',
                      borderColor: alpha(theme.palette.error.main, 0.3),
                    }}
                  />
                )}
                {(agent.shortIdFormatted || agent.shortId) && (
                  <Chip
                    label={`ID ${agent.shortIdFormatted || agent.shortId}`}
                    size="small"
                    variant="outlined"
                    sx={{ height: 20, fontSize: '0.7rem' }}
                  />
                )}
                {agent.requirePassword && (
                  <Chip
                    label="🔒 Con contraseña"
                    size="small"
                    color="warning"
                    variant="outlined"
                    sx={{ height: 20, fontSize: '0.7rem' }}
                  />
                )}
              </Box>
              {lastSeenTime && (
                <Typography variant="caption" color="text.secondary">
                  Último visto: {lastSeenTime}
                </Typography>
              )}
            </Stack>
          </Box>

          <Box
            display="flex"
            alignItems="center"
            gap={0.5}
            sx={{
              mt: viewMode === 'listao' ? 0 : -1,
              mr: -1,
              // The connect + more-vert buttons have a fixed minimum hit
              // area (~36-40px each). Without `flexShrink: 0`, a very long
              // hostname in the middle column can squeeze the icons down
              // and overlap them. Pinning the action cluster's width
              // guarantees the buttons stay tappable on every viewport.
              flexShrink: 0,
            }}
          >
            {isSmallScreen ? (
              <IconButton
                size="small"
                color="primary"
                onClick={handleConnectClick}
                disabled={!agent.connected}
                aria-label="Conectar"
                data-testid="agent-card-connect"
              >
                <LoginIcon fontSize="small" />
              </IconButton>
            ) : (
              <Button
                variant="contained"
                color="primary"
                size="small"
                startIcon={<LoginIcon />}
                onClick={handleConnectClick}
                disabled={!agent.connected}
                sx={{
                  textTransform: 'none',
                  minWidth: 0,
                  px: 1.5,
                }}
                data-testid="agent-card-connect"
              >
                Conectar
              </Button>
            )}
            <IconButton size="small" onClick={handleMenuOpen}>
              <MoreVertIcon />
            </IconButton>
          </Box>
        </CardContent>

        {groups && groups.length > 0 && (
          <Box
            data-testid={`agent-card-assignment-${agent.agentId}`}
            sx={{
              px: 2,
              pb: 1.5,
              pt: 0,
              borderTop: 1,
              borderColor: 'divider',
              // sit on top of CardActionArea; clicking the select must NOT
              // bubble to the card onClick (the select handles stopPropagation
              // internally, but the border + spacing signals "this is separate").
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <GroupAssignmentSelect agent={agent} groups={groups} />
          </Box>
        )}
      </CardActionArea>

      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
        <MenuItem onClick={handleMenuAction(onConnect || (() => {}))}>
          <ListItemIcon>
            <SwapHorizIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="Conectar" />
        </MenuItem>
        <MenuItem onClick={handleMenuAction(onDetails || (() => {}))}>
          <ListItemIcon>
            <InfoIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="Ver detalles" />
        </MenuItem>
        <MenuItem onClick={handleMenuAction(onConsole || (() => {}))}>
          <ListItemIcon>
            <TerminalIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="Modo consola" />
        </MenuItem>
      </Menu>
    </Card>
  );
};
